import React, { useMemo } from 'react';
import { Icon } from '@iconify-icon/react';
import Image from 'next/image';
import Link from 'next/link';

import WhiteLogo from '@/assets/logo-white.png';
import { useCookieConsent } from '@/components/CookieConsentBanner';
import { CmsPageDataTypes } from '@/types/entities/cms.entities';
import PageLink from '@/utils/PageLink';

interface componentProps {
  contentData?: CmsPageDataTypes;
}

type SocialMediaListTypes = {
  title: string;
  url: string;
};

const industries = [
  { label: 'Education & Academia', href: '/use-cases/education' },
  { label: 'Legal & Law Firms', href: '/use-cases/legal' },
  { label: 'Healthcare & Medical', href: '/use-cases/healthcare' },
  { label: 'Financial Services', href: '/use-cases/financial-services' },
  { label: 'Energy & Utilities', href: '/use-cases/energy' },
  { label: 'Technology & SaaS', href: '/use-cases/technology' },
  { label: 'Government', href: '/use-cases/government' },
  { label: 'Non-Profit & Associations', href: '/use-cases/nonprofit' },
  { label: 'Retail & E-Commerce', href: '/use-cases/retail' },
  { label: 'Insurance', href: '/use-cases/insurance' },
  { label: 'Construction & Engineering', href: '/use-cases/construction' },
  { label: 'Hospitality & Tourism', href: '/use-cases/hospitality' },
];

const socialLinks = [
  { href: 'https://x.com/chatdotco', icon: 'prime:twitter', label: 'Twitter' },
  { href: 'https://www.linkedin.com/company/chatco', icon: 'mdi:linkedin', label: 'LinkedIn' },
  { href: 'https://www.instagram.com/chatdotco/', icon: 'mdi:instagram', label: 'Instagram' },
  {
    href: 'https://www.facebook.com/profile.php?id=61588497410599',
    icon: 'mdi:facebook',
    label: 'Facebook',
  },
];

const Footer: React.FC<componentProps> = ({ contentData }) => {
  const { openSettings } = useCookieConsent();
  const memoizedSiteSettingContent = useMemo(
    () => ({
      tagLine: contentData?.settingSection?.tagLine,
      copyRightText: contentData?.settingSection?.copyRightText,
      socialMediaList: Array.isArray(contentData?.settingSection?.links)
        ? (contentData?.settingSection.links as unknown as SocialMediaListTypes[])
        : [],
    }),
    [contentData]
  );

  const socials =
    memoizedSiteSettingContent.socialMediaList.length > 0
      ? memoizedSiteSettingContent.socialMediaList.map((s) => ({
          href: s.url,
          icon: '',
          label: s.title,
        }))
      : socialLinks;

  return (
    <>
      <footer className="relative overflow-hidden bg-[#001A33]">
        {/* Subtle radial glow */}
        <div
          className="pointer-events-none absolute -top-40 left-1/2 h-[500px] w-[900px] -translate-x-1/2 rounded-full opacity-[0.07]"
          style={{
            background: 'radial-gradient(ellipse at center, #0070cc 0%, transparent 70%)',
          }}
        />

        <div className="relative mx-auto max-w-7xl px-6 pb-10 pt-16 sm:px-8 lg:px-10">
          {/* ── Industries strip ── */}
          <div className="mb-14">
            <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#4a7fad]">
              Industries We Serve
            </p>
            <div className="flex flex-wrap gap-2">
              {industries.map((industry) => (
                <Link
                  key={industry.label}
                  href={industry.href}
                  className="rounded-full border border-[#0e3354] bg-[#091f35] px-3.5 py-1.5 text-[12.5px] font-medium text-[#7db4dd] transition-all duration-200 hover:border-[#1a5a8a] hover:bg-[#0d2d4a] hover:text-[#a8d4f5]">
                  {industry.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Gold divider */}
          <div className="mb-12 h-px bg-gradient-to-r from-transparent via-[#D4A84B]/30 to-transparent" />

          {/* ── Main grid ── */}
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-12">
            {/* Brand column */}
            <div className="lg:col-span-4">
              <Link href={PageLink.home} className="inline-block">
                <Image src={WhiteLogo} alt="Chat.co" width={150} height={36} />
              </Link>
              <p className="mt-4 max-w-xs text-[14px] leading-relaxed text-[#5a8bb5]">
                {memoizedSiteSettingContent?.tagLine ||
                  'Turn docs, wikis & data into AI agents that respond with cited, verified answers.'}
              </p>
              <div className="mt-6 flex items-center gap-3">
                {socials.map((s) => (
                  <a
                    key={s.href}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#0e3354] bg-[#091f35] text-[#5a8bb5] transition-all duration-200 hover:border-[#1a5a8a] hover:bg-[#0d2d4a] hover:text-white">
                    {s.icon ? (
                      <Icon icon={s.icon} className="text-lg" />
                    ) : (
                      <span className="text-xs font-medium">{s.label[0]}</span>
                    )}
                  </a>
                ))}
              </div>
            </div>

            {/* Product */}
            <div className="lg:col-span-2">
              <h4 className="mb-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#4a7fad]">
                Product
              </h4>
              <nav className="flex flex-col gap-2.5">
                <FooterLink href={`${PageLink.home}#how-it-works`}>How It Works</FooterLink>
                <FooterLink href="/product">Product Facts</FooterLink>
                <FooterLink href="/ai-chatbot-for-website">Website AI Agent</FooterLink>
                <FooterLink href="/ai-knowledge-base">AI Knowledge Base</FooterLink>
                <FooterLink href="/internal-ai-knowledge-base">Private Team Knowledge</FooterLink>
                <FooterLink href="/customer-support-ai-agent">Customer Support Agent</FooterLink>
                <FooterLink href="/compare/chatbase">Compare Chatbase</FooterLink>
                <FooterLink href="/compare/customgpt">Compare CustomGPT</FooterLink>
                <FooterLink href="/use-cases">Use Cases</FooterLink>
                <FooterLink href={PageLink.schools}>For Schools</FooterLink>
                <FooterLink href={PageLink.companyBrain}>Company Brain</FooterLink>
                <FooterLink href={`${PageLink.home}#customer-stories`}>Customer Stories</FooterLink>
                <FooterLink href={PageLink.pricing}>Pricing</FooterLink>
                <FooterLink href={PageLink.docsHub}>Documentation</FooterLink>
              </nav>
            </div>

            {/* Company */}
            <div className="lg:col-span-2">
              <h4 className="mb-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#4a7fad]">
                Company
              </h4>
              <nav className="flex flex-col gap-2.5">
                <FooterLink href={PageLink.aboutUs}>About Us</FooterLink>
                <FooterLink href={PageLink.press}>Press</FooterLink>
                <FooterLink href={`${PageLink.contactUs}#contact-us`}>Contact</FooterLink>
                <FooterLink href={PageLink.webinars}>Webinars</FooterLink>
                <FooterLink href={PageLink.faqs}>FAQ</FooterLink>
              </nav>
            </div>

            {/* Legal */}
            <div className="lg:col-span-2">
              <h4 className="mb-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#4a7fad]">
                Legal
              </h4>
              <nav className="flex flex-col gap-2.5">
                <FooterLink href={PageLink.terms}>Terms &amp; Conditions</FooterLink>
                <FooterLink href={PageLink.privacy}>Privacy Policy</FooterLink>
                <button
                  type="button"
                  onClick={openSettings}
                  className="text-left text-[13.5px] text-[#5a8bb5] transition-colors duration-150 hover:text-[#a8d4f5]">
                  Cookie Settings
                </button>
              </nav>
            </div>

            {/* Get started CTA */}
            <div className="lg:col-span-2">
              <h4 className="mb-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#4a7fad]">
                Get Started
              </h4>
              <Link
                href={PageLink.login}
                className="group inline-flex items-center gap-2 rounded-lg bg-[#004790] px-5 py-2.5 text-[13px] font-semibold text-white transition-all duration-200 hover:bg-[#0055AF]">
                Start 14-Day Trial
                <svg
                  className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
              <p className="mt-3 text-[12px] text-[#3d6d95]">No credit card required</p>
            </div>
          </div>

          {/* ── Bottom bar ── */}
          <div className="mt-14 border-t border-[#0e3354] pt-6">
            <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
              <p className="text-[12.5px] text-[#3d6d95]">
                {memoizedSiteSettingContent?.copyRightText ||
                  '\u00A9 2026 Chat.co. All rights reserved.'}
              </p>
              <p className="text-[12px] text-[#2a5070]">Built for enterprise knowledge teams.</p>
            </div>
          </div>
        </div>
      </footer>
      {/* Marketing chatbot embed is injected once in _document.tsx (with embed
          route exclusion). A second copy here threw "EMBED_LAUNCHER_DEFAULTS
          has already been declared" and its bot id never won the race. */}
    </>
  );
};

/* ── Tiny helper ── */
function FooterLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      className="text-[13.5px] text-[#5a8bb5] transition-colors duration-150 hover:text-[#a8d4f5]">
      {children}
    </Link>
  );
}

export default Footer;
